import { SingleDataValue } from './SingleDataValue';
import { NormalLines } from './NormalLines';
import { SectionHeading } from './Headings';
import {
    RemoveUnusedDoubleFieldData,
    RemoveUnusedSingleFieldData,
} from './FieldRemovalUtils';
import autoTable from 'jspdf-autotable';

const fieldColumn = (margin, tableWidth, data, reportPdf, options) => {
    let height = 0;

    const autoTableConfig = {
        startY: reportPdf.getCurrentLinePosition(),
        margin: { left: margin },
        tableWidth: tableWidth,
        theme: 'plain',
        columnStyles: {
            0: { halign: 'left', cellWidth: options.labelWidth },
            1: { halign: 'left', lineWidth: 1 },
        },
        body: data,
        didDrawCell: (data) => {
            height = data.cursor.y + data.row.height + 3;
        },
    };

    switch (process.env.REACT_APP_LANGUAGE.toUpperCase()) {
        case 'LAO':
            autoTableConfig.styles = {
                font: 'Phetsarath OT',
            };
            break;
        default:
            break;
    }

    autoTable(reportPdf, autoTableConfig);

    reportPdf.setFont('Times');

    return {
        currentLine: reportPdf.getCurrentLine() + data.length,
        currentLinePosition: height,
    };
};

const singleFieldColumn = (section, data, reportPdf) => {
    if (section.Dynamic) {
        RemoveUnusedSingleFieldData(section, data);
    }

    const tableOne = [];
    for (const row of section.Column1.Fields) {
        const newRow = row.Text
            ? [row.Label, row.Text]
            : [row.Label, SingleDataValue(row.Value, data)];
        tableOne.push(newRow);
    }

    const options = { labelWidth: section.Column1.LabelWidth };
    const positionResult = fieldColumn(
        section.Column1.Left,
        section.Column1.Width,
        tableOne,
        reportPdf,
        options
    );

    reportPdf.setCurrentLine(positionResult.currentLine);
    reportPdf.setCurrentLinePosition(positionResult.currentLinePosition);
};

const singleFieldColumnWithDynamicHeader = (section, data, reportPdf) => {
    section.Heading[0].Text = SingleDataValue(
        section.Column1.Fields[0].Value,
        data
    );

    const fieldCopy = [...section.Column1.Fields];
    section.Column1.Fields.shift();
    if (section.Heading && section.Heading[0].Text) {
        SectionHeading(section.Heading, reportPdf);
    }

    if (section.Column1.Fields.length > 0) {
        singleFieldColumn(section, data, reportPdf);
    }

    section.Column1.Fields = fieldCopy;
};

const doubleFieldColumn = (section, data, reportPdf) => {
    if (section.Dynamic) {
        RemoveUnusedDoubleFieldData(section, data);
    }

    const tableOne = [];
    for (const row of section.Column1.Fields) {
        const newRow = row.Text
            ? [row.Label, row.Text]
            : [row.Label, SingleDataValue(row.Value, data)];
        tableOne.push(newRow);
    }

    let options = { labelWidth: section.Column1.LabelWidth };
    var positionResult1 = fieldColumn(
        section.Column1.Left,
        section.Column1.Width,
        tableOne,
        reportPdf,
        options
    );

    const tableTwo = [];
    for (const row of section.Column2.Fields) {
        const newRow = row.Text
            ? [row.Label, row.Text]
            : [row.Label, SingleDataValue(row.Value, data)];
        tableTwo.push(newRow);
    }

    options = { labelWidth: section.Column2.LabelWidth };
    var positionResult2 = fieldColumn(
        section.Column2.Left,
        section.Column2.Width,
        tableTwo,
        reportPdf,
        options
    );

    reportPdf.setCurrentLine(
        positionResult1.currentLinePosition >
            positionResult2.currentLinePosition
            ? positionResult1.currentLine
            : positionResult2.currentLine
    );
    reportPdf.setCurrentLinePosition(
        positionResult1.currentLinePosition >
            positionResult2.currentLinePosition
            ? positionResult1.currentLinePosition
            : positionResult2.currentLinePosition
    );
};

const doubleFieldColumnWithRowHeading = (section, data, reportPdf) => {
    if (doesDoubleFieldContainData(section, data)) {
        reportPdf.decrementCurrentLinePosition(4);
        NormalLines(section.Heading, reportPdf, data);
        doubleFieldColumn(section, data, reportPdf);
    }
};

const singleFieldColumnWithRowHeading = (section, data, reportPdf) => {
    if (doesSingleFieldContainData(section, data)) {
        reportPdf.decrementCurrentLinePosition(4);
        NormalLines(section.Heading, reportPdf, data);
        singleFieldColumn(section, data, reportPdf);
        reportPdf.setSavedSectionHeading(undefined);
    }
};

const singleFieldColumnWithSeparateHeading = (section, data, reportPdf) => {
    if (doesSingleFieldContainData(section, data)) {
        if (section.Heading && section.Heading[0].Text) {
            SectionHeading(section.Heading, reportPdf);
        }

        singleFieldColumn(section, data, reportPdf);
        reportPdf.setSavedSectionHeading(undefined);
    }
};

const hasNonEmptyField = (fields, data) =>
    fields.some((row) => SingleDataValue(row.Value, data) !== '');

const doesSingleFieldContainData = (section, data) =>
    hasNonEmptyField(section.Column1.Fields, data);

const doesDoubleFieldContainData = (section, data) =>
    hasNonEmptyField(section.Column1.Fields, data) ||
    hasNonEmptyField(section.Column2.Fields, data);

export {
    doubleFieldColumn as DoubleFieldColumn,
    doubleFieldColumnWithRowHeading as DoubleFieldColumnWithRowHeading,
    singleFieldColumn as SingleFieldColumn,
    singleFieldColumnWithDynamicHeader as SingleFieldColumnWithDynamicHeader,
    singleFieldColumnWithRowHeading as SingleFieldColumnWithRowHeading,
    singleFieldColumnWithSeparateHeading as SingleFieldColumnWithSeparateHeading,
    doesDoubleFieldContainData as DoesDoubleFieldContainData,
};
